import { Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectorRef,
  ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  firstGridRowObjectFromPayload,
} from '../../../../shared/workspace-element-context';
import { CbcDeleteDataByMeService } from './cbc-delete-data-by-me.service';

@Component({
    selector: 'gtw-cbc-delete-data-by-me',
    templateUrl: './cbc-delete-data-by-me.component.html',
    styleUrls: ['./cbc-delete-data-by-me.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CbcDeleteDataByMeComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();

  modalTitle = 'Clear CbCr Data By ME';
  minimise = false;
  isSaveClicked = false;
  deleting = false;

  clearTrialBalance = false;
  clearManualAdj = false;

  showConfirmation = false;
  confirmConfig: ConfirmationModalConfig = {
    title: 'Delete Data',
    message: 'Are you sure you want to clear the data?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...',
  };

  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  rowData: any = {};
  private isInitialized = false;

  constructor(
    private deleteService: CbcDeleteDataByMeService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const raw = rowPayloadFromActiveScreen(activeScreen);
    const resolved = firstGridRowObjectFromPayload(raw);
    if (resolved && Object.keys(resolved).length > 0) {
      this.rowData = resolved;
    }
    this.cdr.markForCheck();
  }

  private get ssoId(): string {
    return this.USER_SETTINGS?.user?.sso_id ?? '';
  }

  private get baseUrl(): string {
    return this.GENERAL_CONFIG?.base_url ?? '';
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (
      !this.GENERAL_CONFIG?.base_url ||
      !this.filterParams ||
      Object.keys(this.filterParams).length === 0 ||
      !this.globalService ||
      Object.keys(this.globalService).length === 0
    ) {
      return;
    }
    this.isInitialized = true;
    this.cdr.markForCheck();
  }

  get isDeleteDisabled(): boolean {
    return (!this.clearTrialBalance && !this.clearManualAdj) || this.deleting;
  }

  private getTtKey(): string {
    if (this.clearTrialBalance && this.clearManualAdj) {
      return '244,245';
    }
    return this.clearTrialBalance ? '244' : '245';
  }

  save(): void {
    if (this.isDeleteDisabled) return;
    this.showConfirmation = true;
    this.cdr.detectChanges();
  }

  onConfirm(): void {
    this.showConfirmation = false;
    this.delete();
  }

  onCancelConfirm(): void {
    this.showConfirmation = false;
    this.cdr.detectChanges();
  }

  delete(): void {
    if (this.isDeleteDisabled || !this.rowData?.MAINTENANCE_OWNER) return;
    this.isSaveClicked = true;
    this.deleting = true;
    this.confirmConfig = { ...this.confirmConfig, loading: true };
    this.cdr.detectChanges();

    const ttKey = this.getTtKey();

    this.deleteService
      .deleteCbcDataByMe(
        this.baseUrl,
        this.ssoId,
        this.rowData.MAINTENANCE_OWNER,
        ttKey,
        this.globalService,
        this.filterParams
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isSaveClicked = false;
          this.deleting = false;
          this.confirmConfig = { ...this.confirmConfig, loading: false };
          if (response?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar(
              'Data deleted successfully.',
              'gtw-snackbar--green'
            );
            this.emitRefreshAndClose();
          } else {
            this.gtwSnackbarService.openSnackBar(
              response?.errorMessage || 'Error deleting data.',
              'gtw-snackbar--red'
            );
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.isSaveClicked = false;
          this.deleting = false;
          this.confirmConfig = { ...this.confirmConfig, loading: false };
          this.gtwSnackbarService.openSnackBar(
            err?.message || 'Something went wrong while deleting the data, please try again!',
            'gtw-snackbar--red'
          );
          this.cdr.detectChanges();
        },
      });
  }

  private emitRefreshAndClose(): void {
    this.dispatchUpdate('refresh');
    setTimeout(() => {
      const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen;
      if (activeScreen?.screen_key) {
        this.gtwWorkspace.refreshGridData.next(activeScreen);
      }
    }, 500);
  }

  cancel(): void {
    this.dispatchUpdate('cancel');
  }

  private dispatchUpdate(detail: 'refresh' | 'cancel'): void {
    const updateEvent = new CustomEvent('request-grid-update', {
      detail,
      bubbles: true,
    });
    document
      .querySelector('gtw-cbc-delete-data-by-me-ce')
      ?.dispatchEvent(updateEvent);
  }
}
